import { Injectable } from '@angular/core';
import { Validators, FormGroup, FormArray } from '@angular/forms';

import {
  minMaxRangeValidator,
  noHTMLWhitespaceValidator,
  noWhitespaceValidator,
  positiveNumberValidator,
} from '@core/common-legacy';
import {
  BooleanFieldControls,
  CustomFieldFormGroup,
  CustomFieldType,
  DateFieldControls,
  MoneyFieldControls,
  MultiChoiceFieldControls,
  MultiUserFieldControls,
  NumberFieldControls,
  SingleChoiceFieldControls,
  SingleLineFieldControls,
  SingleUserFieldControls,
} from '@core/custom-fields-types';

@Injectable()
export class CustomFieldsValidationService {
  public updateValidators(
    customFields: FormArray<CustomFieldFormGroup>,
    shouldSetValidators: boolean = true,
  ): void {
    if (!customFields) {
      return;
    }

    customFields.controls.forEach(control => {
      const isRequired = control.controls.required.value && shouldSetValidators;
      const type = control.controls.type.value;
      switch (control.controls.type.value) {
        case CustomFieldType.SingleLine:
          this.updateSingleLineValidators(control, isRequired);
          return;
        case CustomFieldType.SingleChoice:
          this.updateSingleChoiceValidators(control, isRequired);
          return;
        case CustomFieldType.MultiChoice:
          this.updateMultiChoiceValidators(control, isRequired);
          return;
        case CustomFieldType.Boolean:
          this.updateBooleanValidators(control, isRequired);
          return;
        case CustomFieldType.Number:
          this.updateNumberValidators(control, isRequired);
          return;
        case CustomFieldType.Money:
          this.updateMoneyValidators(control, isRequired);
          return;
        case CustomFieldType.Date:
          this.updateDateValidators(control, isRequired);
          return;
        case CustomFieldType.SingleUser:
          this.updateSingleUserValidators(control, isRequired);
          return;
        case CustomFieldType.MultiUser:
          this.updateMultiUserValidators(control, isRequired);
          return;

        default:
          throw new Error(`Unsupported custom field type: ${type}`);
      }
    });
  }

  private updateSingleLineValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const valueSingleLineControl = (<FormGroup<SingleLineFieldControls>>control).controls.value
      .controls.value;
    isRequired
      ? valueSingleLineControl.setValidators([Validators.required, noHTMLWhitespaceValidator])
      : valueSingleLineControl.clearValidators();
    valueSingleLineControl.updateValueAndValidity();
  }

  private updateSingleChoiceValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const optionControl = (<FormGroup<SingleChoiceFieldControls>>control).controls.value.controls
      .option;
    isRequired
      ? optionControl.setValidators([Validators.required])
      : optionControl.clearValidators();
    optionControl.updateValueAndValidity();
  }

  private updateMultiChoiceValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const optionsControl = (<FormGroup<MultiChoiceFieldControls>>control).controls.value.controls
      .options;
    isRequired
      ? optionsControl.setValidators([Validators.required])
      : optionsControl.clearValidators();
    optionsControl.updateValueAndValidity();
  }

  private updateBooleanValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const valueBooleanControl = (<FormGroup<BooleanFieldControls>>control).controls.value.controls
      .value;
    isRequired
      ? valueBooleanControl.setValidators([Validators.required])
      : valueBooleanControl.clearValidators();
    valueBooleanControl.updateValueAndValidity();
  }

  private updateNumberValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const numberControls = (<FormGroup<NumberFieldControls>>control).controls.value.controls;
    if (isRequired) {
      numberControls.min?.addValidators([Validators.required]);
      numberControls.max?.addValidators([Validators.required]);
      numberControls.value?.addValidators([Validators.required]);
    } else {
      numberControls.min?.clearValidators();
      numberControls.max?.clearValidators();
      numberControls.value?.clearValidators();
      numberControls.min?.addValidators([
        noWhitespaceValidator,
        positiveNumberValidator,
        minMaxRangeValidator('min', 'max'),
      ]);
      numberControls.max?.addValidators([
        noWhitespaceValidator,
        positiveNumberValidator,
        minMaxRangeValidator('min', 'max'),
      ]);
      numberControls.value?.addValidators([noWhitespaceValidator, positiveNumberValidator]);
    }
    numberControls.min?.updateValueAndValidity();
    numberControls.max?.updateValueAndValidity();
    numberControls.value?.updateValueAndValidity();
  }

  private updateMoneyValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const moneyControls = (<FormGroup<MoneyFieldControls>>control).controls.value.controls;
    if (isRequired) {
      moneyControls.min?.addValidators([Validators.required]);
      moneyControls.max?.addValidators([Validators.required]);
      moneyControls.value?.addValidators([Validators.required]);
      moneyControls.currency.addValidators([Validators.required]);
    } else {
      moneyControls.min?.clearValidators();
      moneyControls.max?.clearValidators();
      moneyControls.value?.clearValidators();
      moneyControls.currency.clearValidators();
      moneyControls.min?.addValidators([
        noWhitespaceValidator,
        positiveNumberValidator,
        minMaxRangeValidator('min', 'max'),
      ]);
      moneyControls.max?.addValidators([
        noWhitespaceValidator,
        positiveNumberValidator,
        minMaxRangeValidator('min', 'max'),
      ]);
      moneyControls.value?.addValidators([noWhitespaceValidator, positiveNumberValidator]);
    }
    moneyControls.min?.updateValueAndValidity();
    moneyControls.max?.updateValueAndValidity();
    moneyControls.value?.updateValueAndValidity();
    moneyControls.currency.updateValueAndValidity();
  }

  private updateDateValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const valueDateControl = (<FormGroup<DateFieldControls>>control).controls.value.controls.value;
    isRequired
      ? valueDateControl.setValidators([Validators.required])
      : valueDateControl.clearValidators();
    valueDateControl.updateValueAndValidity();
  }

  private updateSingleUserValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const adminSingleUserControl = (<FormGroup<SingleUserFieldControls>>control).controls.value
      .controls.admin;
    isRequired
      ? adminSingleUserControl.setValidators([Validators.required])
      : adminSingleUserControl.clearValidators();
    adminSingleUserControl.updateValueAndValidity();
  }

  private updateMultiUserValidators(control: CustomFieldFormGroup, isRequired: boolean): void {
    const adminsMultiUserControl = (<FormGroup<MultiUserFieldControls>>control).controls.value
      .controls.admins;
    isRequired
      ? adminsMultiUserControl.setValidators([Validators.required])
      : adminsMultiUserControl.clearValidators();
    adminsMultiUserControl.updateValueAndValidity();
  }
}
